import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft, Check, ChevronRight, Copy, Download, ExternalLink, Image as ImageIcon, Linkedin, LoaderCircle, Lock, RefreshCw,
  Share2, Sparkles, TriangleAlert, Upload, X,
} from 'lucide-react';
import { TeamsLogo } from '../BrandIcons';
import { articleUrl } from '../../utils/articles';
import { aiErrorInfo, articleService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { LinkPreviewThumb, previewDescription } from './LinkPreview';
import './SharePopover.css';

const POST_MAX = 3000;
const MOBILE = '(max-width: 600px)';

function useMedia(query) {
  const [match, setMatch] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setMatch(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, [query]);
  return match;
}

/**
 * Compartilhar artigo (1a). Tela inicial: LinkedIn com o texto do post (opcionalmente gerado pela IA), Teams,
 * "Imagem e texto" e o link. Tela 2: o post com IA (Autor | Recomendando), a capa para baixar/copiar e as saídas.
 * Desktop: popover de 360px ancorado no botão; celular: bottom sheet.
 */
export function SharePopover({ article, onShare, ActionButton, size = 'md' }) {
  const { user } = useAuth();
  const mobile = useMedia(MOBILE);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('menu');
  const [copied, setCopied] = useState(null);
  const [toast, setToast] = useState(null);
  const [voice, setVoice] = useState(() => (user && article.author?.id === user.id ? 'author' : 'recommend'));
  // Post: idle (sem texto) | writing | ready | error. O texto já leva o link no fim.
  const [post, setPost] = useState({ status: 'idle', text: '' });
  const [canShareFile, setCanShareFile] = useState(false);
  const ref = useRef(null);

  const isPublic = article.visibility === 'public';
  const url = articleUrl(article);
  const domain = window.location.host;
  const local = /^(localhost|127\.|0\.0\.0\.0|\[::1\])/.test(window.location.hostname);
  const aiEnabled = !!user && !user.demo;
  // Capa do autor; sem ela, a gerada (pública para artigo público, autenticada para "Só alunos")
  const image = article.coverUrl || (isPublic && !user
    ? `/api/public/articles/${article.slug}/og.png`
    : `/api/articles/${article.id}/card.png`);

  useEffect(() => {
    try {
      const probe = new File([new Blob([''], { type: 'image/png' })], 'x.png', { type: 'image/png' });
      setCanShareFile(!!navigator.canShare && navigator.canShare({ files: [probe] }));
    } catch {
      setCanShareFile(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!mobile && ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    if (mobile) document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      if (mobile) document.body.style.overflow = '';
    };
  }, [open, mobile]);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(null), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const close = () => setOpen(false);
  const toggle = () => {
    setView('menu');
    setOpen(o => !o);
  };

  const generate = async (v = voice) => {
    setPost(p => ({ ...p, status: 'writing' }));
    try {
      const text = await articleService.sharePost(article.id, v);
      setPost({ status: 'ready', text: `${text.trim()}\n\n${url}` });
    } catch (err) {
      setPost(p => ({ status: 'error', text: p.text, error: aiErrorInfo(err).message }));
    }
  };

  const changeVoice = (v) => {
    setVoice(v);
    // Texto gerado muda junto com a voz
    if (post.status === 'ready' && post.generated !== false) generate(v);
  };

  const copyText = async (value, what) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(what);
      onShare?.(what === 'link' ? 'link' : 'kit');
      return true;
    } catch {
      return false;
    }
  };

  const postLinkedIn = async () => {
    const text = post.status === 'ready' && post.text.trim() ? post.text : url;
    // O LinkedIn nem sempre preenche o texto pela URL: deixamos copiado para colar
    if (post.status === 'ready' && post.text.trim()) {
      if (await copyText(text, 'text')) setToast('Texto copiado: se o post abrir vazio, é só colar.');
    }
    window.open(`https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer,width=680,height=720');
    onShare?.('linkedin');
  };

  const postTeams = () => {
    window.open(`https://teams.microsoft.com/share?href=${encodeURIComponent(url)}&msgText=${encodeURIComponent(article.title)}`,
      '_blank', 'noopener,noreferrer,width=680,height=720');
    onShare?.('teams');
    close();
  };

  /** A imagem em PNG (a área de transferência só aceita PNG; capas podem ser JPG/WebP). */
  const imagePng = async () => {
    const blob = await (await fetch(image, { credentials: 'include' })).blob();
    if (blob.type === 'image/png') return blob;
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0);
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  };

  const copyImage = async () => {
    try {
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': await imagePng() })]);
      setCopied('image');
      onShare?.('kit');
    } catch {
      setToast('Seu navegador não deixou copiar a imagem. Use "Baixar imagem".');
    }
  };

  const shareNative = async () => {
    try {
      const file = new File([await imagePng()], `${article.slug}.png`, { type: 'image/png' });
      await navigator.share({ files: [file], text: post.text || url, title: article.title });
      onShare?.('kit');
    } catch (err) {
      if (err?.name !== 'AbortError') setToast('Não deu para abrir o compartilhamento. Copie a imagem e o texto.');
    }
  };

  const panel = view === 'menu' ? (
    <MenuView
      article={article} isPublic={isPublic} url={url} domain={domain} local={local} post={post} aiEnabled={aiEnabled}
      copied={copied} onClose={close} onPostLinkedIn={postLinkedIn} onGenerate={() => generate()} onEdit={() => setView('kit')}
      onRemove={() => setPost({ status: 'idle', text: '' })} onTeams={postTeams} onKit={() => setView('kit')}
      onCopyLink={() => copyText(url, 'link')}
    />
  ) : (
    <KitView
      article={article} isPublic={isPublic} image={image} post={post} setPost={setPost} voice={voice} aiEnabled={aiEnabled}
      copied={copied} canShareFile={canShareFile} url={url} onBack={() => setView('menu')} onClose={close}
      onVoice={changeVoice} onGenerate={() => generate()} onCopyText={() => copyText(post.text, 'text')} onCopyImage={copyImage}
      onDownload={() => onShare?.('kit')} onShareNative={shareNative} onPostLinkedIn={postLinkedIn}
    />
  );

  return (
    <span className="shp" ref={ref}>
      <ActionButton icon={Share2} size={size} className={open ? 'is-open' : ''} onClick={toggle} aria-expanded={open}>
        Compartilhar
      </ActionButton>
      {open && !mobile && <div className="shp-pop" role="dialog" aria-label="Compartilhar artigo">{panel}</div>}
      {open && mobile && createPortal(
        <div className="shp-sheet-backdrop" onMouseDown={(e) => e.target === e.currentTarget && close()}>
          <div className="shp-sheet" role="dialog" aria-modal="true" aria-label="Compartilhar artigo">
            <span className="shp-sheet__handle" aria-hidden="true" />
            {panel}
          </div>
        </div>,
        document.body,
      )}
      {toast && createPortal(<div className="shp-toast" role="status">{toast}</div>, document.body)}
    </span>
  );
}

function Head({ title, onBack, onClose }) {
  return (
    <div className="shp-head">
      {onBack && <button type="button" className="shp-iconbtn" onClick={onBack} aria-label="Voltar"><ArrowLeft size={16} aria-hidden="true" /></button>}
      <span className="shp-head__title">{title}</span>
      <button type="button" className="shp-iconbtn" onClick={onClose} aria-label="Fechar"><X size={16} aria-hidden="true" /></button>
    </div>
  );
}

function MenuView({
  article, isPublic, url, domain, local, post, aiEnabled, copied, onClose, onPostLinkedIn, onGenerate, onEdit, onRemove,
  onTeams, onKit, onCopyLink,
}) {
  return (
    <>
      <Head title="Compartilhar" onClose={onClose} />
      <div className="shp-body">
        <div className="shp-mini">
          <LinkPreviewThumb article={article} className="shp-mini__thumb" />
          <span className="shp-mini__text">
            <strong>{article.title}</strong>
            <span>{domain}</span>
          </span>
        </div>

        {isPublic ? (
          <div className="shp-li">
            <div className="shp-li__row">
              <span className="shp-brand shp-brand--li"><Linkedin size={17} aria-hidden="true" /></span>
              <span className="shp-row__text"><strong>LinkedIn</strong><span>Abre o post com o texto e o link</span></span>
              <button type="button" className="shp-btn shp-btn--li" onClick={onPostLinkedIn}>
                Postar<ExternalLink size={14} aria-hidden="true" />
              </button>
            </div>
            {aiEnabled && (
              post.status === 'writing' ? (
                <div className="shp-li__strip is-writing"><LoaderCircle size={14} className="shp-spin" aria-hidden="true" />Escrevendo o texto…</div>
              ) : post.status === 'ready' ? (
                <div className="shp-li__strip is-ready">
                  <Sparkles size={14} aria-hidden="true" /><span>Texto gerado com IA vai junto</span>
                  <button type="button" className="shp-link" onClick={onEdit}>Editar</button>
                  <button type="button" className="shp-link is-muted" onClick={onRemove}>Remover</button>
                </div>
              ) : post.status === 'error' ? (
                <div className="shp-li__strip is-error">
                  <TriangleAlert size={14} aria-hidden="true" /><span>A IA não respondeu. Dá para postar só com o link.</span>
                  <button type="button" className="shp-link" onClick={onGenerate}>Tentar de novo</button>
                </div>
              ) : (
                <div className="shp-li__strip">
                  <Sparkles size={14} aria-hidden="true" /><span>Opcional: um texto pronto para o post</span>
                  <button type="button" className="shp-link" onClick={onGenerate}>Gerar com IA</button>
                </div>
              )
            )}
          </div>
        ) : (
          <div className="shp-li is-locked">
            <div className="shp-li__row">
              <span className="shp-brand shp-brand--lock"><Lock size={15} aria-hidden="true" /></span>
              <span className="shp-row__text"><strong>LinkedIn</strong><span>Só alunos: quem vê no LinkedIn não tem conta. Torne o artigo público para postar.</span></span>
            </div>
          </div>
        )}

        <button type="button" className="shp-row" onClick={onTeams}>
          <span className="shp-brand shp-brand--teams"><TeamsLogo size={18} /></span>
          <span className="shp-row__text"><strong>Microsoft Teams</strong><span>Envia para uma conversa ou canal</span></span>
          <ExternalLink size={14} aria-hidden="true" className="shp-row__end" />
        </button>
        <button type="button" className="shp-row" onClick={onKit}>
          <span className="shp-brand shp-brand--kit"><ImageIcon size={16} aria-hidden="true" /></span>
          <span className="shp-row__text"><strong>Imagem e texto</strong><span>{aiEnabled ? 'Capa para baixar e o post com IA para copiar' : 'Capa para baixar e o texto para copiar'}</span></span>
          <ChevronRight size={16} aria-hidden="true" className="shp-row__end" />
        </button>

        {!isPublic && (
          <p className="shp-note shp-note--navy"><Lock size={14} aria-hidden="true" />Só alunos: quem abrir o link sem conta vai para o login.</p>
        )}
        {local && isPublic && (
          <p className="shp-note shp-note--amber"><TriangleAlert size={14} aria-hidden="true" />Rodando em localhost: a prévia não aparece no LinkedIn/Teams.</p>
        )}
      </div>
      <div className="shp-foot">
        <span className="shp-url" title={url}>{url}</span>
        <button type="button" className={`shp-btn shp-btn--navy ${copied === 'link' ? 'is-done' : ''}`} onClick={onCopyLink}>
          {copied === 'link' ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
          {copied === 'link' ? 'Copiado' : 'Copiar'}
        </button>
      </div>
    </>
  );
}

function KitView({
  article, isPublic, image, post, setPost, voice, aiEnabled, copied, canShareFile, url, onBack, onClose, onVoice, onGenerate,
  onCopyText, onCopyImage, onDownload, onShareNative, onPostLinkedIn,
}) {
  const hasText = post.status === 'ready' && post.text.trim().length > 0;
  return (
    <>
      <Head title="Imagem e texto" onBack={onBack} onClose={onClose} />
      <div className="shp-body">
        {aiEnabled && (
          <div className="shp-voice">
            <span>Você vai postar como</span>
            <div className="shp-seg" role="radiogroup" aria-label="Você vai postar como">
              {[['author', 'Autor'], ['recommend', 'Recomendando']].map(([v, l]) => (
                <button key={v} type="button" role="radio" aria-checked={voice === v} className={voice === v ? 'is-active' : ''}
                        onClick={() => onVoice(v)} disabled={post.status === 'writing'}>{l}</button>
              ))}
            </div>
          </div>
        )}

        {post.status === 'writing' ? (
          <div className="shp-post is-writing" aria-live="polite">
            <span className="shp-post__writing"><Sparkles size={14} aria-hidden="true" />Escrevendo<span className="shp-dots"><i /><i /><i /></span></span>
            {[100, 92, 96, 62, 88, 70].map((w, i) => <span key={i} className="shp-skel" style={{ width: `${w}%` }} />)}
          </div>
        ) : post.status === 'error' ? (
          <div className="shp-post is-error" role="alert">
            <strong><TriangleAlert size={15} aria-hidden="true" />Não consegui escrever o post agora</strong>
            <span>{post.error || 'A IA não respondeu.'} Tente de novo ou escreva o seu.</span>
            <div className="shp-post__actions">
              <button type="button" className="shp-btn shp-btn--danger-outline" onClick={onGenerate}>Tentar de novo</button>
              <button type="button" className="shp-link is-muted" onClick={() => setPost({ status: 'ready', text: `\n\n${url}`, generated: false })}>Escrever sem IA</button>
            </div>
          </div>
        ) : post.status === 'ready' ? (
          <div className="shp-post is-ready">
            <textarea value={post.text} maxLength={POST_MAX} aria-label="Texto do post"
                      onChange={(e) => setPost({ ...post, text: e.target.value, generated: post.generated })} />
            <div className="shp-post__foot">
              <span>{post.text.length.toLocaleString('pt-BR')} / {POST_MAX.toLocaleString('pt-BR')}</span>
              {aiEnabled && (
                <button type="button" className="shp-link shp-link--lime" onClick={onGenerate}>
                  <RefreshCw size={13} aria-hidden="true" />Gerar outro
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="shp-post is-empty">
            <span>{aiEnabled ? 'Gere um rascunho a partir do artigo, ou escreva o seu.' : 'Escreva o texto do seu post.'}</span>
            {aiEnabled && (
              <button type="button" className="shp-btn shp-btn--navy shp-btn--lg" onClick={onGenerate}>
                <Sparkles size={15} aria-hidden="true" className="shp-lime" />Gerar post com IA
              </button>
            )}
            <button type="button" className="shp-link is-muted"
                    onClick={() => setPost({ status: 'ready', text: `${article.title}\n\n${previewDescription(article)}\n\n${url}`, generated: false })}>
              Escrever do zero
            </button>
          </div>
        )}

        <div className="shp-image">
          <img src={image} alt={`Capa de ${article.title}`} />
          <div className="shp-image__actions">
            <a className="shp-link shp-link--lime" href={image} download={`${article.slug}.png`} onClick={onDownload}>
              <Download size={14} aria-hidden="true" />Baixar imagem
            </a>
            <button type="button" className={`shp-link shp-link--lime ${copied === 'image' ? 'is-done' : ''}`} onClick={onCopyImage}>
              {copied === 'image' ? <Check size={14} aria-hidden="true" /> : <ImageIcon size={14} aria-hidden="true" />}
              {copied === 'image' ? 'Imagem copiada' : 'Copiar imagem'}
            </button>
            <span>Cole com Ctrl+V no LinkedIn ou no Teams</span>
          </div>
        </div>
      </div>
      <div className="shp-foot shp-foot--kit">
        {canShareFile && (
          <button type="button" className="shp-iconbtn shp-iconbtn--box" onClick={onShareNative} aria-label="Compartilhar…" title="Compartilhar…">
            <Upload size={16} aria-hidden="true" />
          </button>
        )}
        <button type="button" className={`shp-btn shp-btn--outline ${copied === 'text' ? 'is-done' : ''}`} onClick={onCopyText} disabled={!hasText}>
          {copied === 'text' ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
          {copied === 'text' ? 'Texto copiado' : 'Copiar texto'}
        </button>
        <button type="button" className="shp-btn shp-btn--li" onClick={onPostLinkedIn} disabled={!isPublic}
                title={isPublic ? undefined : 'Só alunos: torne o artigo público para postar'}>
          {isPublic ? <Linkedin size={14} aria-hidden="true" /> : <Lock size={14} aria-hidden="true" />}Postar no LinkedIn
        </button>
      </div>
    </>
  );
}
